<template>
  <multiselect
    id="ajax"
    v-model="selectedCompanies"
    label="name"
    track-by="name"
    placeholder="Type to search for companies"
    open-direction="bottom"
    :options="companies"
    :multiple="true"
    :loading="isLoading"
    :clear-on-select="false"
    :close-on-select="false"
    :searchable="true"
    :internal-search="false"
    :show-no-results="false"
    :hide-selected="true"
    :taggable="true"
    tag-placeholder="Enter to search for this company"
    :max-height="600"
    @tag="addTag"
    @search-change="debouncedSearchCompany"
    @input="handleInput"
    @remove="handleRemove"
  >
    <span
      slot="afterList"
      v-observe-visibility="onVisibilityChanged"
    >
      <hr>
    </span>
  </multiselect>
</template>

<script>
import Multiselect from "vue-multiselect";
import {isEmpty, debounce} from "lodash";
import { ObserveVisibility } from "vue-observe-visibility";
import axios from "axios";

export default {
    name: "CompanyNameSelect",
    components: {Multiselect},
    directives: {ObserveVisibility},
    data() {
        return {
            companyTags: [],
            companyFilters: [],
            searchUrl: "/admin/firm/api-get-companies",
            selectedCompanies: [],
            companies: [],
            isLoading: false,
            searchQuery: null,
            page: 1,
            axiosCancelSource: null,
        };
    },
    watch: {
        companyTags(tags) {
            this.$emit("input", tags.join(" "));
        },
        companyFilters(filters) {
            //trim whitespace
            const companyFilters = filters.map(filter => filter.trim());
            
            this.$emit("update-filter", companyFilters);
        },
    },
    created() {
        // Debounce setup - keeps methods clean, assign to Vue's instance
        this.debouncedSearchCompany = debounce(this.searchCompanyHandler, 500, {maxWait: 1000});
    },
    methods: {
        /** Returns currently selected tag strings. */
        getSelectedTags() {
            return this.companyTags;
        },
        /** Adds a new company tag and pushes to selection. */
        addTag(newTag) {
            if (
                newTag.length < 2 ||
                this.selectedCompanies.some(item => item.name === newTag)
            ) {
                return;
            }
            this.companyTags.push(newTag);
            this.selectedCompanies.push({
                name: newTag,
                id: `tag_${newTag.slice(0, 2)}${Math.floor(Math.random() * 10000000)}`,
                type: "tag",
            });
        },
        /** Emits only company names (not tag items) for parent filter. */
        handleInput(options) {
            this.companyFilters = options
                .filter(option => option.type === undefined)
                .map(option => option.name);
        },
        /** Removes tag from companyTags if the removed option is a tag. */
        handleRemove(removedOption) {
            if (removedOption.type === "tag") {
                this.companyTags = this.companyTags.filter(tag => tag !== removedOption.name);
            }
        },
        /** Debounced handler for search changes. */
        searchCompanyHandler(searchQuery) {
            if (isEmpty(searchQuery)) {
                this.companies = [];
                return;
            }
            // Reset params
            this.searchQuery = searchQuery;
            this.companies = [];
            this.page = 1;
            this.loadCompanies(searchQuery, 1);
        },
        /** Loads companies from API with search, handles cancelation and pagination. */
        async loadCompanies(searchQuery, pageNum = this.page) {
            this.isLoading = true;
            // Cancel any pending requests
            if (this.axiosCancelSource) {
                this.axiosCancelSource.cancel("Operation canceled due to new request.");
            }
            this.axiosCancelSource = axios.CancelToken.source();
            try {
                const response = await axios.get(this.searchUrl, {
                    cancelToken: this.axiosCancelSource.token,
                    params: {
                        search: searchQuery,
                        pagination: 20,
                        page: pageNum,
                    },
                });
                this.companies = [...this.companies, ...response.data.data];
            } catch (e) {
                if (!axios.isCancel(e)) {
                    // Handle errors except cancellation
                    console.error("Error loading companies", e);
                }
            } finally {
                this.isLoading = false;
            }
        },
        /** Loads next page of companies for infinite scroll. */
        loadMore() {
            this.page += 1;
            this.loadCompanies(this.searchQuery, this.page);
        },
        /** Handler for visibility change (infinite scroll trigger). */
        onVisibilityChanged(isVisible) {
            if (isVisible) {
                this.loadMore();
            }
        },
        /** Returns display string for additional selection count. */
        limitText(count) {
            return `and ${count} other companies`;
        },
        clear() {
            this.selectedCompanies = [];
            this.companyTags = [];
            this.companyFilters = [];
            this.companies = [];
            this.searchQuery = null;
            this.page = 1;
        },
    },
};
</script>

<style scoped>
</style>
